'use client';

// One hook feeding the redesigned /saved grid. It merges the two Supabase sources —
// `saved_elements` (via useSavedElements) and `favorites` (fetched here) — into a single
// normalized list, and exposes the organisational mutators (rename, folder, tags, pin) that all
// route through saved_elements.metadata. Favorites are read-only reference bookmarks here except
// for removal.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/app/context/AuthContext';
import { createSupabaseBrowserClient } from '@/app/lib/supabase/client';
import { deriveSaveKey, useSavedElements } from '@/app/hooks/useSavedElements';
import { toLibraryItems, type Favorite, type LibraryItem } from '@/app/saved/lib/library';

/** A player UUID, dashed or not — the form Mojang's API returns. */
const UUID_KEY = /^[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}$/;

/** Keys that identify a PLAYER rather than a name: `saveProfile`'s bare uuid and
 *  `savePlayerSkin`'s `skin-<uuid>`. Shape-matched rather than type-matched, because the `skin`
 *  type covers BOTH an uploaded texture (name-keyed) and a player's skin (uuid-keyed). */
function isIdentityKey(key: string): boolean {
  return UUID_KEY.test(key) || UUID_KEY.test(key.replace(/^skin-/, ''));
}

export function useSavedLibrary() {
  const { user } = useAuth();
  const { elements, loading: elementsLoading, updateElement, duplicateElement, removeElement } = useSavedElements();

  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      // Deliberate: drop the previous user's favorites the moment auth clears (mirrors
      // useSavedElements / useFavorites). Safe — it runs only on the user→null transition.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFavorites([]);
      // Clear the in-flight flag too: the cleanup below cancels any response still on its way,
      // and only that response path resets it, so a sign-out mid-query would otherwise leave
      // `loading` true for the rest of the session.
      setFavoritesLoading(false);
      return;
    }
    // Same cross-user guard as the other hooks: a late response for a previous user must not
    // repopulate the grid after logout / account switch.
    let cancelled = false;
    setFavoritesLoading(true);
    const supabase = createSupabaseBrowserClient();
    supabase
      .from('favorites')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error) setFavorites((data as Favorite[]) ?? []);
        setFavoritesLoading(false);
      });
    return () => { cancelled = true; };
  }, [user]);

  const items = useMemo(() => toLibraryItems(elements, favorites), [elements, favorites]);

  // Distinct, user-ordered-then-alpha folder list (element items only carry folders in v1).
  const folders = useMemo(() => {
    const set = new Set<string>();
    for (const it of items) if (it.folder) set.add(it.folder);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const it of items) it.tags.forEach(t => set.add(t));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [items]);

  // ── organisational mutators (element-only; favorites are silently ignored) ──

  const rename = useCallback(
    (item: LibraryItem, name: string) => {
      if (item.source !== 'element') return Promise.resolve({ error: null });
      const patch: { display_name: string; metadata?: Record<string, unknown> } = { display_name: name };
      // Carry the dedupe key along with the name. Leaving it behind made the build invisible to
      // its own editor (`isSaved` matches on the key), so saving the same design again inserted a
      // SECOND row and the library showed two cards with one name.
      //
      // ⚠️ NOT for the two identity-keyed types. `saveProfile` keys on the player's bare UUID and
      // `savePlayerSkin` on `skin-<uuid>` — those identify a PLAYER, not a name, and rewriting one
      // to a name would break the match the editor makes against Mojang's id. Every other helper
      // derives its key from the name, so every other row travels with it.
      const currentKey = (item.metadata as { key?: unknown }).key;
      if (typeof currentKey === 'string' && !isIdentityKey(currentKey)) {
        patch.metadata = { key: deriveSaveKey(name) };
      }
      return updateElement(item.id, patch);
    },
    [updateElement],
  );

  const setFolder = useCallback(
    async (elementIds: number[], folder: string | null): Promise<{ error: string | null }> => {
      const results = await Promise.all(elementIds.map(id => updateElement(id, { metadata: { folder } })));
      // Surface the first real error rather than swallowing partial failures. (An RLS-blocked
      // UPDATE still returns error:null — that class is covered by the required UPDATE policy.)
      return { error: results.find(r => r.error)?.error ?? null };
    },
    [updateElement],
  );

  const setTags = useCallback(
    (item: LibraryItem, tags: string[]) =>
      item.source === 'element'
        ? updateElement(item.id, { metadata: { tags } })
        : Promise.resolve({ error: null }),
    [updateElement],
  );

  const setPinned = useCallback(
    (item: LibraryItem, pinned: boolean) =>
      item.source === 'element'
        ? updateElement(item.id, { metadata: { pinned } })
        : Promise.resolve({ error: null }),
    [updateElement],
  );

  // Folder-level operations. Folders have no table of their own — they are just a value in each
  // member's metadata — so renaming/deleting a folder is a bulk write over its members. Deleting a
  // folder moves its builds to Unfiled (clears the field); it never deletes the builds themselves.
  const renameFolder = useCallback(
    async (from: string, to: string) => {
      const ids = items.filter(it => it.source === 'element' && it.folder === from).map(it => it.id);
      // Returned, not swallowed: a partial failure snaps those cards back, and the page must say why.
      return setFolder(ids, to);
    },
    [items, setFolder],
  );

  const deleteFolder = useCallback(
    async (name: string) => {
      const ids = items.filter(it => it.source === 'element' && it.folder === name).map(it => it.id);
      return setFolder(ids, null);
    },
    [items, setFolder],
  );

  const removeFavorite = useCallback(
    async (id: number) => {
      if (!user) return;
      // Capture the removed row inside the updater and roll back ONLY that row on error (mirrors
      // useSavedElements.removeElement). Restoring a whole-array snapshot would resurrect any
      // favorite a concurrent delete removed in the meantime.
      let removed: Favorite | undefined;
      setFavorites(list => {
        removed = list.find(f => f.id === id);
        return list.filter(f => f.id !== id);
      });
      const supabase = createSupabaseBrowserClient();
      const { error } = await supabase.from('favorites').delete().eq('id', id).eq('user_id', user.id);
      if (error && removed) setFavorites(list => [...list, removed!]);
    },
    [user],
  );

  // Unified delete that dispatches to the right table by source.
  const remove = useCallback(
    (item: LibraryItem) => (item.source === 'element' ? removeElement(item.id) : removeFavorite(item.id)),
    [removeElement, removeFavorite],
  );

  const duplicate = useCallback(
    (item: LibraryItem) => (item.source === 'element' ? duplicateElement(item.id) : Promise.resolve({ error: null })),
    [duplicateElement],
  );

  return {
    items,
    loading: elementsLoading || favoritesLoading,
    folders,
    allTags,
    rename,
    setFolder,
    renameFolder,
    deleteFolder,
    setTags,
    setPinned,
    remove,
    duplicate,
    updateElement,
  };
}
